import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { ExpressCheckoutPartialAddress } from '@stripe/stripe-js'
import { type ShippingOption } from '../../stores/shippingStore/shipping.types'
import { mergeProductsShippingOptions } from '../../features/productArrivals/cartShippingOptions'
import { getProductDeliveryPromiseQueryOptions } from '../../features/productArrivals/deliveryPromisesQuery'
import { useDeliveryPromisesQueryInputs } from '../../features/productArrivals/hooks/useDeliveryPromisesQueryInputs'
import { createPartialAddressFromStripeFormat } from '../StripeAddressForm/stripeAdapter'
import { logger } from '../../services/logger'
import { shippingOptionsManager } from '../../services/ShippingOptionsManager'
import { useCheckout } from './CheckoutContext'

export type ExpressCheckoutDeliveryOptions = {
  options: ShippingOption[]
  hasFailed: boolean
}

export default function useExpressCheckoutShippingOptionsFetcher() {
  const queryClient = useQueryClient()
  const { isGroupOrder } = useCheckout()
  const deliveryPromisesQueryInputs = useDeliveryPromisesQueryInputs()

  return useCallback(
    async (
      stripeAddress: ExpressCheckoutPartialAddress
    ): Promise<ExpressCheckoutDeliveryOptions> => {
      // Only cart orders are sent with a delivery promise.
      if (!isGroupOrder) {
        return { options: [], hasFailed: false }
      }
      try {
        const address = createPartialAddressFromStripeFormat(stripeAddress)
        const { deliveryPromiseRequests, ...sharedInputs } =
          deliveryPromisesQueryInputs
        const productsShippingOptions = await Promise.all(
          deliveryPromiseRequests.map(async (deliveryPromiseRequest) => ({
            productType: deliveryPromiseRequest.productType,
            shippingOptions: await queryClient.fetchQuery(
              getProductDeliveryPromiseQueryOptions({
                ...sharedInputs,
                address,
                deliveryPromiseRequest,
              })
            ),
          }))
        )
        const shippingOptions = mergeProductsShippingOptions(
          productsShippingOptions
        )
        shippingOptionsManager.recordShownShippingOptions({
          shippingOptions,
          persist: false,
        })
        return { options: shippingOptions, hasFailed: false }
      } catch (error) {
        logger.error('Express checkout shipping options failed', error)
        return { options: [], hasFailed: true }
      }
    },
    [queryClient, isGroupOrder, deliveryPromisesQueryInputs]
  )
}
